/**
 * Constants for Fixed Fees module
 * Centralized magic strings and mapping objects for consistency and performance
 */

// Default empty value display
export const EMPTY_VALUE = '—';

// Completion state enum
export const CompletionState = {
  NotCompleted: 0,
  Completed: 1
};

// Status Mappings
export const STATUS_MAP = {
  notcompleted: 'Not Completed'
};
export const BILLED_STATUS_MAP = {
  wip: 'WIP',
  final: 'Final',
  interim: 'Interim',
  notfinal: 'Not Final'
};
export const CHARGE_TYPE_MAP = {
  DateBased: 'Date-based',
  Recurring: 'Recurring',
  CompletionBased: 'Completion-based'
};

// Color Mappings
export const STATUS_COLORS = {
  unbilled: 'primary',
  billed: 'grey-lighten-1',
  active: 'success',
  complete: 'grey-lighten-1',
  overdue: 'error'
};
export const COMPLETION_COLORS = {
  complete: 'success',
  incomplete: 'grey-lighten-1',
  'n/a': 'grey-lighten-2'
};

// Profitability thresholds
export const PROFITABILITY_THRESHOLDS = {
  LOW: 10,
  NEGATIVE: 0
};
export const PROFITABILITY_COLORS = {
  NEGATIVE: 'text-red-600',
  LOW: 'text-orange-500',
  GOOD: 'text-green-600',
  DEFAULT: 'text-gray-400'
};

// The Lock Time request's wipDisposition (BR-012). Serialises as the backend's
// WipDisposition enum, camelCase on the wire. Omitted entirely when no target
// fee has unbilled entries.
export const WipDisposition = {
  MarkBilled: 'markBilled',
  LeaveInWorkflow: 'leaveInWorkflow'
};

// Column id as the /columns endpoint reports it, and the key the CSV export
// keys on (ChargeCsvSchema).
export const TIME_LOCK_COLUMN_ID = 'timeLocked';

// A Time Lock status is only meaningful on a billed fee, so choosing one
// auto-populates the Billed filter (UI-015, BR-020). 'Final' is the value the
// Billed Status filter labels "Billed" — see LlBilledStatusSelect.
//
// Known limit: `billed` is really `final` OR `interim`, and BilledState holds
// one value, so this hides fees billed onto a draft invoice (`interim`). Widen
// it here if BilledState ever gains a value covering both.
export const BILLED_FILTER_VALUE = 'Final';

// Time Lock status filter (UI-001). Kept as its own vocabulary rather than a
// raw bool so 'all' is representable; queryParams maps it to the API's bool?.
export const TIME_LOCK_FILTER = {
  ALL: 'all',
  LOCKED: 'locked',
  UNLOCKED: 'unlocked'
};
export const TIME_LOCK_FILTER_OPTIONS = [{
  id: TIME_LOCK_FILTER.ALL,
  label: 'All'
}, {
  id: TIME_LOCK_FILTER.UNLOCKED,
  label: 'Time Unlocked'
}, {
  id: TIME_LOCK_FILTER.LOCKED,
  label: 'Time Locked'
}];

// Filter constants
export const FILTER_ALL = 'all';
export const FILTER_WIP = 'WIP';
export const FILTER_ANY = 'any';

// ============================================================================
// Skeleton Loader Specifications
// ============================================================================

/**
 * Fixed Fee Form Skeleton Specification
 * Defines the loading skeleton layout for CreateChargeForm
 */
export const FIXED_FEE_FORM_SKELETON = [
// Tabs
{
  type: 'chip@3',
  class: 'border-b-2 border-gray-300 mb-4 pb-2'
},
// Client/Matter row
{
  type: 'row',
  spacing: 'space-x-4',
  class: 'w-full',
  columns: [{
    type: 'text',
    class: 'w-1/2'
  }, {
    type: 'text',
    class: 'w-1/2'
  }]
},
// Fixed Fee Template and Amount row
{
  type: 'row',
  spacing: 'space-x-4',
  class: 'w-full',
  columns: [{
    type: 'text',
    class: 'w-1/2'
  }, {
    type: 'text',
    class: 'w-1/4'
  }, {
    type: 'text',
    class: 'w-1/4'
  }]
},
// Charge type card
{
  type: 'card',
  class: 'mb-4',
  style: 'height: 160px'
},
// Description
{
  type: 'paragraph',
  class: 'mb-4'
},
// Custom flag and recurring options
{
  type: 'card',
  class: 'mb-4',
  style: 'height: 60px'
},
// Additional fields row
{
  type: 'row',
  spacing: 'space-x-4',
  class: 'w-full',
  columns: [{
    type: 'text',
    class: 'w-1/3'
  }, {
    type: 'text',
    class: 'w-1/3'
  }, {
    type: 'text',
    class: 'w-1/3'
  }]
}];
export const BulkUpdateType = {
  DELETE: 'delete',
  MARK_COMPLETE: 'mark complete',
  MARK_INCOMPLETE: 'mark incomplete',
  CHANGE_MATTER: 'change matter',
  MAKE_BILLABLE: 'make billable',
  MAKE_NON_BILLABLE: 'make non-billable',
  CHANGE_FEE: 'change fee',
  LOCK_TIME: 'lock time',
  UNLOCK_TIME: 'unlock time'
};

/**
 * Time Entries Tab Skeleton Specification
 * Defines the loading skeleton layout for FixedFeeTimeEntriesTab with accordion sections
 */
export const TIME_ENTRIES_SKELETON = [
// WIP Section header
{
  type: 'heading',
  class: 'mb-2'
},
// WIP Table rows
{
  type: 'table-row-divider@5',
  class: 'w-full mb-4'
},
// Billed/Invoiced Section header
{
  type: 'heading',
  class: 'mb-2'
},
// Billed Table rows
{
  type: 'table-row-divider@3',
  class: 'w-full'
}];